import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Page = styled.div`
  min-height: 100vh;
  background: #f0f6ff;
  padding: 28px 18px 90px;
`;

const Container = styled.div`
  width: min(1180px, 100%);
  margin: 0 auto;
`;

const Hero = styled.section`
  background: linear-gradient(135deg, #991b1b, #ef4444 48%, #f97316);
  border-radius: 28px;
  padding: 28px;
  color: white;
  box-shadow: 0 24px 60px rgba(220, 38, 38, 0.24);
  margin-bottom: 18px;

  h1 {
    margin: 0;
    font-size: clamp(1.7rem, 4vw, 2.6rem);
    font-weight: 950;
    letter-spacing: -0.04em;
  }

  p {
    margin: 10px 0 0;
    max-width: 760px;
    color: rgba(255, 255, 255, 0.88);
    line-height: 1.6;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  margin: 18px 0;
`;

const StatCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 16px;
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.06);

  .label {
    color: #64748b;
    font-size: 0.78rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .value {
    color: #0f172a;
    font-size: 1.8rem;
    font-weight: 950;
    margin-top: 6px;
  }
`;

const Toolbar = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 14px;
  display: grid;
  gap: 12px;
  margin-bottom: 16px;

  @media (min-width: 820px) {
    grid-template-columns: 1.4fr 1fr auto;
    align-items: end;
  }
`;

const Field = styled.label`
  display: grid;
  gap: 6px;
  color: #334155;
  font-size: 0.78rem;
  font-weight: 800;
`;

const Input = styled.input`
  height: 42px;
  border: 1.5px solid #dbeafe;
  border-radius: 12px;
  padding: 0 12px;
  color: #0f172a;
  font-weight: 700;
  outline: none;

  &:focus {
    border-color: #ef4444;
    box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.12);
  }
`;

const Select = styled.select`
  height: 42px;
  border: 1.5px solid #dbeafe;
  border-radius: 12px;
  padding: 0 12px;
  color: #0f172a;
  font-weight: 800;
  background: white;
  outline: none;
`;

const OrgPanel = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
`;

const OrgCard = styled.div`
  border: 1px solid #fed7aa;
  background: linear-gradient(135deg, #fff7ed, #ffffff);
  border-radius: 18px;
  padding: 14px;

  h3 {
    margin: 0 0 10px;
    color: #9a3412;
    font-size: 0.95rem;
    font-weight: 900;
  }

  .row {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    color: #64748b;
    font-size: 0.8rem;
    margin-top: 6px;
  }

  strong { color: #0f172a; }
`;

const TableCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 22px;
  overflow: hidden;
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.06);
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 920px;

  th {
    text-align: left;
    color: #475569;
    background: #f8fafc;
    font-size: 0.74rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 13px 14px;
  }

  td {
    padding: 14px;
    border-top: 1px solid #f1f5f9;
    color: #334155;
    font-size: 0.86rem;
    vertical-align: top;
  }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 9px;
  font-size: 0.72rem;
  font-weight: 900;
  color: ${p => p.$level === 'critical' ? '#991b1b' : '#92400e'};
  background: ${p => p.$level === 'critical' ? '#fee2e2' : '#fef3c7'};
  border: 1px solid ${p => p.$level === 'critical' ? '#fecaca' : '#fde68a'};
`;

const TypePill = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 9px;
  font-size: 0.72rem;
  font-weight: 900;
  color: ${p => p.$org ? '#075985' : '#166534'};
  background: ${p => p.$org ? '#e0f2fe' : '#dcfce7'};
`;

const ActionBtn = styled.button`
  border: 0;
  border-radius: 10px;
  padding: 8px 11px;
  font-size: 0.76rem;
  font-weight: 900;
  color: white;
  background: linear-gradient(135deg, #dc2626, #f97316);
  cursor: pointer;
`;

const Empty = styled.div`
  padding: 42px 18px;
  text-align: center;
  color: #64748b;

  h3 {
    margin: 0 0 8px;
    color: #0f172a;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(15, 23, 42, 0.58);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
`;

const Modal = styled.div`
  width: min(720px, 100%);
  max-height: 82vh;
  overflow: auto;
  background: white;
  border-radius: 24px;
  box-shadow: 0 28px 80px rgba(15, 23, 42, 0.28);
`;

const ModalHead = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  gap: 14px;

  h2 {
    margin: 0;
    color: #0f172a;
    font-size: 1.05rem;
  }
`;

const ModalBody = styled.div`
  padding: 20px;
  display: grid;
  gap: 14px;
`;

const CloseBtn = styled.button`
  border: 0;
  background: #f1f5f9;
  color: #334155;
  font-weight: 900;
  border-radius: 10px;
  padding: 8px 11px;
  cursor: pointer;
  height: fit-content;
`;

const ReasonList = styled.ul`
  margin: 0;
  padding-left: 18px;
  color: #475569;
  line-height: 1.55;
`;

const DetailSection = styled.div`
  display: grid;
  gap: 10px;

  h3 {
    margin: 0;
    color: #0f172a;
    font-size: 0.95rem;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
`;

const DetailTile = styled.div`
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  border-radius: 14px;
  padding: 12px;

  span {
    display: block;
    color: #64748b;
    font-size: 0.72rem;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 5px;
  }

  strong {
    color: #0f172a;
    font-size: 0.9rem;
  }
`;

const ScoreGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
`;

const ScoreTile = styled.div`
  border: 1px solid #fee2e2;
  background: #fff7ed;
  border-radius: 14px;
  padding: 12px;

  .label { color: #9a3412; font-size: 0.78rem; font-weight: 900; }
  .score { color: #0f172a; font-size: 1.35rem; font-weight: 950; margin-top: 4px; }
  .severity { color: #b45309; font-size: 0.78rem; font-weight: 900; margin-top: 2px; }
`;

const DescriptionBox = styled.div`
  border-left: 4px solid #ef4444;
  background: #fef2f2;
  border-radius: 14px;
  padding: 12px;
  color: #475569;
  line-height: 1.55;
`;

const RecommendationList = styled.div`
  display: grid;
  gap: 10px;
`;

const RecommendationItem = styled.div`
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 14px;
  padding: 12px;

  strong {
    color: #9a3412;
    display: block;
    font-size: 0.84rem;
    margin-bottom: 5px;
  }

  p {
    color: #334155;
    line-height: 1.5;
    margin: 0;
    white-space: pre-line;
  }
`;

const ResponseList = styled.div`
  display: grid;
  gap: 8px;
`;

const ResponseItem = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  align-items: start;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 10px;
  color: #334155;
  font-size: 0.84rem;

  strong {
    color: #0f172a;
    white-space: nowrap;
  }
`;

const formatDate = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const getClientName = (result) => {
  const name = `${result.Client?.firstName || ''} ${result.Client?.lastName || ''}`.trim();
  return name || result.TestAssignment?.clientName || 'Unnamed client';
};

const getScoreEntries = (interpretation) => {
  if (!interpretation || typeof interpretation !== 'object') return [];
  return Object.entries(interpretation)
    .filter(([key, value]) => key && !key.startsWith('_') && value && typeof value === 'object' && ('score' in value || 'severity' in value));
};

const parseJsonValue = (value, fallback) => {
  if (typeof value !== 'string') return value ?? fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const getQuestionItems = (questions) => {
  const parsedQuestions = parseJsonValue(questions, []);
  return Array.isArray(parsedQuestions) ? parsedQuestions : [];
};

const formatDetailText = (value) => {
  if (value === null || value === undefined || value === '') return '';
  if (Array.isArray(value)) return value.map(formatDetailText).filter(Boolean).join('\n');
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

const extractDisplayValue = (value) => {
  if (Array.isArray(value)) return value.map(formatDetailText).join(', ');
  if (value && typeof value === 'object') {
    if (Object.prototype.hasOwnProperty.call(value, 'label')) return formatDetailText(value.label);
    if (Object.prototype.hasOwnProperty.call(value, 'value')) return formatDetailText(value.value);
    if (Object.prototype.hasOwnProperty.call(value, 'answer')) return formatDetailText(value.answer);
    if (Object.prototype.hasOwnProperty.call(value, 'response')) return formatDetailText(value.response);
    return formatDetailText(value);
  }
  return value === null || value === undefined ? '—' : formatDetailText(value);
};

const getResponseValue = (responses, question, index) => {
  const parsedResponses = parseJsonValue(responses, null);
  if (!parsedResponses) return '—';

  const candidates = [question?.id, question?.questionId, question?.question_id, question?.order, index + 1]
    .filter(value => value !== null && value !== undefined && value !== '');

  if (Array.isArray(parsedResponses)) {
    const matchedResponse = parsedResponses.find(response => {
      if (!response || typeof response !== 'object') return false;
      return candidates.some(candidate => [response.questionId, response.question_id, response.id, response.order].some(value => String(value) === String(candidate)));
    });

    if (matchedResponse) return extractDisplayValue(matchedResponse);
    if (parsedResponses[index] !== undefined) return extractDisplayValue(parsedResponses[index]);
    return '—';
  }

  for (const candidate of candidates) {
    if (Object.prototype.hasOwnProperty.call(parsedResponses, candidate)) return extractDisplayValue(parsedResponses[candidate]);
    const stringCandidate = String(candidate);
    if (Object.prototype.hasOwnProperty.call(parsedResponses, stringCandidate)) return extractDisplayValue(parsedResponses[stringCandidate]);
  }

  return '—';
};

const getDetailItems = (value) => {
  const parsedValue = parseJsonValue(value, value);
  if (Array.isArray(parsedValue)) {
    return parsedValue.map((item, index) => ({ title: `Item ${index + 1}`, text: formatDetailText(item) })).filter(item => item.text);
  }
  if (parsedValue && typeof parsedValue === 'object') {
    return Object.entries(parsedValue).map(([title, text]) => ({ title, text: formatDetailText(text) })).filter(item => item.text);
  }
  const text = formatDetailText(parsedValue);
  return text ? [{ title: '', text }] : [];
};

const FlaggedFollowUps = () => {
  const [results, setResults] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [linkType, setLinkType] = useState('all');
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    loadFlaggedResults();
  }, []);

  const loadFlaggedResults = async () => {
    try {
      const res = await axios.get(`${API_URL}/practitioner/follow-up-flags`);
      setResults(res.data.results || []);
      setSummary(res.data.summary || null);
    } catch (error) {
      console.error('Flagged follow-up load error:', error);
    } finally {
      setLoading(false);
    }
  };


  const viewDetails = async (result) => {
    setSelected(result);
    setDetailLoading(true);
    try {
      const detailResultId = result.detailResultId || result.id;
      const res = await axios.get(`${API_URL}/practitioner/results/${detailResultId}`);
      setSelected({ ...result, ...res.data, detailResultId, followUpRisk: result.followUpRisk || res.data.followUpRisk });
    } catch (error) {
      console.error('Flagged follow-up detail load error:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const filteredResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    return results.filter(result => {
      const isOrg = result.TestAssignment?.linkType === 'organization';
      if (linkType === 'organization' && !isOrg) return false;
      if (linkType === 'individual' && isOrg) return false;

      if (!q) return true;
      const haystack = [
        getClientName(result),
        result.Client?.email,
        result.TestCatalog?.shortName,
        result.TestCatalog?.name,
        result.TestAssignment?.organizationName,
        result.followUpRisk?.recommendedAction,
        ...(result.followUpRisk?.reasons || [])
      ].join(' ').toLowerCase();

      return haystack.includes(q);
    });
  }, [results, search, linkType]);

  return (
    <Page>
      <Container>
        <Hero>
          <h1>Flagged Follow-Up Tracker</h1>
          <p>
            Track clients whose results need priority follow-up, split by organization links and individual links so you do not have to search through the general results page.
          </p>
        </Hero>

        <StatsGrid>
          <StatCard><div className="label">Total flagged</div><div className="value">{summary?.total || 0}</div></StatCard>
          <StatCard><div className="label">Critical</div><div className="value">{summary?.critical || 0}</div></StatCard>
          <StatCard><div className="label">Organization links</div><div className="value">{summary?.organizationLinks || 0}</div></StatCard>
          <StatCard><div className="label">Individual links</div><div className="value">{summary?.individualLinks || 0}</div></StatCard>
        </StatsGrid>

        <Toolbar>
          <Field>
            Search flagged clients, organizations, tests, or reasons
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search follow-up flags" />
          </Field>
          <Field>
            Link tracking view
            <Select value={linkType} onChange={e => setLinkType(e.target.value)}>
              <option value="all">All flagged clients</option>
              <option value="organization">Organization links only</option>
              <option value="individual">Individual links only</option>
            </Select>
          </Field>
          <ActionBtn type="button" onClick={loadFlaggedResults}>Refresh</ActionBtn>
        </Toolbar>

        {summary?.organizations?.length > 0 && linkType !== 'individual' && (
          <OrgPanel>
            {summary.organizations.map(org => (
              <OrgCard key={org.organizationName}>
                <h3>{org.organizationName}</h3>
                <div className="row"><span>Total flagged</span><strong>{org.total}</strong></div>
                <div className="row"><span>Critical</span><strong>{org.critical}</strong></div>
                <div className="row"><span>High</span><strong>{org.high}</strong></div>
                <div className="row"><span>Latest flag</span><strong>{formatDate(org.latestFlaggedAt)}</strong></div>
              </OrgCard>
            ))}
          </OrgPanel>
        )}

        <TableCard>
          {loading ? (
            <Empty><h3>Loading flagged clients…</h3><p>Checking the latest follow-up risks.</p></Empty>
          ) : filteredResults.length === 0 ? (
            <Empty><h3>No flagged clients found</h3><p>Try changing the search or link-type filter.</p></Empty>
          ) : (
            <TableScroll>
              <Table>
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Test</th>
                    <th>Risk</th>
                    <th>Link tracking</th>
                    <th>Reason</th>
                    <th>Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredResults.map(result => {
                    const isOrg = result.TestAssignment?.linkType === 'organization';
                    return (
                      <tr key={result.id}>
                        <td>
                          <strong>{getClientName(result)}</strong><br />
                          <span>{result.Client?.email || result.TestAssignment?.clientEmail || 'No email'}</span>
                        </td>
                        <td>{result.TestCatalog?.shortName || result.TestCatalog?.name || 'Assessment'}</td>
                        <td><Badge $level={result.followUpRisk?.level}>{result.followUpRisk?.level || 'flagged'}</Badge></td>
                        <td>
                          <TypePill $org={isOrg}>{isOrg ? 'Organization' : 'Individual'}</TypePill><br />
                          <span>{isOrg ? (result.TestAssignment?.organizationName || 'Unnamed organization') : 'Direct client link'}</span>
                        </td>
                        <td>{result.followUpRisk?.reasons?.[0] || result.followUpRisk?.recommendedAction}</td>
                        <td>{formatDate(result.completedAt)}</td>
                        <td><ActionBtn type="button" onClick={() => viewDetails(result)}>View Details</ActionBtn></td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </TableScroll>
          )}
        </TableCard>
      </Container>

      {selected && (
        <ModalOverlay role="dialog" aria-modal="true" aria-labelledby="flagged-detail-title">
          <Modal>
            <ModalHead>
              <div>
                <h2 id="flagged-detail-title">{getClientName(selected)}</h2>
                <div>{selected.TestCatalog?.name || selected.TestCatalog?.shortName || 'Assessment'} • {formatDate(selected.completedAt)}</div>
              </div>
              <CloseBtn type="button" onClick={() => setSelected(null)}>Close</CloseBtn>
            </ModalHead>
            <ModalBody>
              <div><Badge $level={selected.followUpRisk?.level}>{selected.followUpRisk?.recommendedAction}</Badge></div>
              <div>
                <strong>Link tracking:</strong>{' '}
                {selected.TestAssignment?.linkType === 'organization'
                  ? `Organization — ${selected.TestAssignment?.organizationName || 'Unnamed organization'}`
                  : 'Individual link'}
              </div>
              <div>
                <strong>Client contact:</strong> {selected.Client?.email || selected.TestAssignment?.clientEmail || 'No email recorded'}
              </div>
              <div>
                <strong>Flag reasons</strong>
                <ReasonList>
                  {(selected.followUpRisk?.reasons?.length ? selected.followUpRisk.reasons : [selected.followUpRisk?.recommendedAction]).map((reason, idx) => (
                    <li key={idx}>{reason}</li>
                  ))}
                </ReasonList>
              </div>

              {detailLoading ? (
                <DetailSection><h3>Loading result details…</h3></DetailSection>
              ) : (
                <>
                  <DetailSection>
                    <h3>Client demographics</h3>
                    <DetailGrid>
                      <DetailTile><span>Age</span><strong>{selected.Client?.age || '—'}</strong></DetailTile>
                      <DetailTile><span>Gender</span><strong>{selected.Client?.genderCustom || selected.Client?.gender || '—'}</strong></DetailTile>
                      <DetailTile><span>Phone</span><strong>{selected.Client?.phone || '—'}</strong></DetailTile>
                      <DetailTile><span>Occupation</span><strong>{selected.Client?.occupation || '—'}</strong></DetailTile>
                      <DetailTile><span>Education</span><strong>{selected.Client?.education || '—'}</strong></DetailTile>
                      <DetailTile><span>Marital status</span><strong>{selected.Client?.maritalStatus || '—'}</strong></DetailTile>
                    </DetailGrid>
                  </DetailSection>

                  {getScoreEntries(selected.interpretation).length > 0 && (
                    <DetailSection>
                      <h3>Result scores</h3>
                      <ScoreGrid>
                        {getScoreEntries(selected.interpretation).map(([key, value]) => (
                          <ScoreTile key={key}>
                            <div className="label">{key}</div>
                            <div className="score">{value.score ?? '—'}</div>
                            <div className="severity">{value.severity || 'No severity'}</div>
                          </ScoreTile>
                        ))}
                      </ScoreGrid>
                    </DetailSection>
                  )}

                  {getDetailItems(selected.interpretation?._description).length > 0 && (
                    <DetailSection>
                      <h3>Interpretation</h3>
                      <DescriptionBox>{getDetailItems(selected.interpretation?._description).map(item => item.text).join('\n\n')}</DescriptionBox>
                    </DetailSection>
                  )}

                  {getDetailItems(selected.interpretation?._recommendations).length > 0 && (
                    <DetailSection>
                      <h3>Recommendations</h3>
                      <RecommendationList>
                        {getDetailItems(selected.interpretation?._recommendations).map((item, index) => (
                          <RecommendationItem key={`${item.title}-${index}`}>
                            {item.title && <strong>{item.title}</strong>}
                            <p>{item.text}</p>
                          </RecommendationItem>
                        ))}
                      </RecommendationList>
                    </DetailSection>
                  )}

                  {getQuestionItems(selected.TestCatalog?.questions).length > 0 && (
                    <DetailSection>
                      <h3>Client responses</h3>
                      <ResponseList>
                        {getQuestionItems(selected.TestCatalog?.questions).map((question, index) => (
                          <ResponseItem key={question.id || index}>
                            <span>{index + 1}. {formatDetailText(question.text) || 'Question'}</span>
                            <strong>{getResponseValue(selected.responses, question, index)}</strong>
                          </ResponseItem>
                        ))}
                      </ResponseList>
                    </DetailSection>
                  )}
                </>
              )}
            </ModalBody>
          </Modal>
        </ModalOverlay>
      )}
    </Page>
  );
};

export default FlaggedFollowUps;
